// =============================================================================
// The mobile navigation drawer.
//
// CSS does the sliding (`.mobile-nav.is-open`) and the hamburger's morph into a
// cross (`.hamburger.is-open`); this module owns the open state and keeps the
// three things that have to agree with it in step: the backdrop, the button's
// `aria-expanded`, and the page scroll lock.
// =============================================================================

import { lockScroll, releaseScroll, closeOnEscape } from "./overlay.js";

export default function initMobileNav() {
	const toggle = document.querySelector("[data-open-nav]");
	const nav = document.querySelector("[data-mobile-nav]");

	if (!toggle || !nav) {
		return;
	}

	const backdrop = document.querySelector("[data-nav-backdrop]");
	const closeBtn = document.querySelector("[data-close-nav]");

	let open = false;

	function setOpen(next) {
		// Guarding on the current state is what makes this safe to call from
		// Escape, which closes every overlay whether or not it was open.
		// Without it an already-closed drawer would release a scroll lock
		// belonging to the search panel.
		if (next === open) {
			return;
		}
		open = next;

		toggle.classList.toggle("is-open", open);
		nav.classList.toggle("is-open", open);
		if (backdrop) {
			backdrop.classList.toggle("is-open", open);
		}
		toggle.setAttribute("aria-expanded", open ? "true" : "false");

		if (open) {
			lockScroll();
			// Focus follows the drawer, so tabbing starts inside it rather than
			// on the page now hidden behind it, and returns to the button that
			// opened it on the way out.
			(closeBtn || nav).focus();
		} else {
			releaseScroll();
			toggle.focus();
		}
	}

	toggle.addEventListener("click", () => setOpen(!open));
	if (closeBtn) {
		closeBtn.addEventListener("click", () => setOpen(false));
	}
	if (backdrop) {
		backdrop.addEventListener("click", () => setOpen(false));
	}
	closeOnEscape(() => setOpen(false));

	// A drawer row carrying a sub-list is a disclosure, not a link: the design
	// gives the parent no page of its own beyond the shop, which the sub-list
	// already ends with. Rows without one navigate, and close the drawer on the
	// way so a back-navigation does not arrive with it still open.
	//
	// Delegated on the drawer rather than bound per row, because the markup
	// comes from inc/nav.php and this must keep working if that grows.
	nav.addEventListener("click", (event) => {
		const disclosure = event.target.closest("[data-mobile-toggle]");

		if (disclosure && nav.contains(disclosure)) {
			event.preventDefault();
			disclosure.closest(".mobile-item")?.classList.toggle("is-open");
			disclosure.setAttribute(
				"aria-expanded",
				disclosure.getAttribute("aria-expanded") === "true" ? "false" : "true",
			);
			return;
		}

		// Only a real navigation closes the drawer; a click on the drawer's own
		// chrome should leave it exactly as it was.
		if (event.target.closest("a")) {
			setOpen(false);
		}
	});
}
